"use client";

import React, { useState } from "react";
import { HiCheck } from "react-icons/hi";
import { LuClipboardCopy } from "react-icons/lu";

import { useUser } from "@/contexts/user-provider";

import { truncateId } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";

interface IdDisplayProps {
  id: string;
  label?: string;
  copyDisabled?: boolean;
  className?: string;
  truncate?: boolean;
  /** When true, always show the ID even when user's showIds setting is off */
  alwaysShow?: boolean;
}

const IdDisplay: React.FC<IdDisplayProps> = ({
  id,
  label,
  copyDisabled = false,
  truncate = true,
  className,
  alwaysShow = false,
}) => {
  const { currentUser } = useUser();
  const [isCopied, setIsCopied] = useState(false);

  if (!alwaysShow && !currentUser?.showIds) {
    return null;
  }

  // Stop the click here. This renders inside clickable rows in a dozen
  // tables — quotes, products, forms, devices — where letting it bubble
  // means copying an id also fires the row's action and navigates away
  // from the thing the user was copying. Nothing wants both.
  const handleCopy = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!copyDisabled) {
      navigator.clipboard.writeText(id).then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      });
    }
  };

  // Always treat id as a string
  const displayId = truncate ? truncateId(id.toString()) : id.toString();

  return (
    <div className={cx("flex items-center", className)}>
      {label && <span className="mr-1">{label}: </span>}
      <span>{displayId}</span>
      {!copyDisabled &&
        (isCopied ? (
          <HiCheck className="ml-1.5 size-4 text-green-500" />
        ) : (
          <LuClipboardCopy
            className="ml-1.5 size-3 shrink-0 cursor-pointer"
            onClick={handleCopy}
          />
        ))}
    </div>
  );
};

export default IdDisplay;
